/**
 * Profile.js - User Profile Page
 * 
 * Allows users to view and edit their profile including:
 * - Name and username
 * - Dark mode toggle
 * - Header theme color
 * - Self-service password change (with current password verification)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import dataStore from '../data/dataStore';

function Profile() {
  const { user, refreshUser } = useAuth();
  const { theme, colors, darkMode, toggleDarkMode, headerColorOptions, customHeaderColor, setHeaderColor } = useTheme();
  
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Availability state (for admins only)
  const [availabilityStart, setAvailabilityStart] = useState('08:00');
  const [availabilityEnd, setAvailabilityEnd] = useState('17:00');

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setUsername(user.username || '');
      // Load availability for admins
      if (user.role === 'ADMIN') {
        setAvailabilityStart(user.availabilityStart || '08:00');
        setAvailabilityEnd(user.availabilityEnd || '17:00');
      }
    }
  }, [user]);

  // Handle profile update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const updates = { name, username };
      // Include availability for admins
      if (user.role === 'ADMIN') {
        updates.availabilityStart = availabilityStart;
        updates.availabilityEnd = availabilityEnd;
      }
      await dataStore.updateUser(user.id, updates, user);
      refreshUser();
      setMessage('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message);
    }
  };

  // Calculate password strength
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: '', color: theme.textSecondary };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    
    if (score <= 1) return { level: 1, label: 'Weak', color: colors.red };
    if (score <= 2) return { level: 2, label: 'Fair', color: colors.orange };
    if (score <= 3) return { level: 3, label: 'Good', color: colors.yellow };
    return { level: 4, label: 'Strong', color: colors.green };
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    // Validation
    if (!currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }
    if (!newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      await dataStore.changeOwnPassword(user.id, currentPassword, newPassword);
      setPasswordMessage('Password changed successfully!');
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
      // Auto-clear success message
      setTimeout(() => setPasswordMessage(''), 5000);
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordError(err.message);
    }
  };

  // Styles
  const containerStyle = {
    padding: '20px',
    maxWidth: '600px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    padding: '30px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`
  };

  const titleStyle = {
    color: theme.text,
    marginBottom: '30px',
    marginTop: 0
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    color: theme.text,
    fontWeight: 'bold'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    marginBottom: '20px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    backgroundColor: theme.background,
    color: theme.text,
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    padding: '12px 24px',
    backgroundColor: colors.green,
    color: darkMode ? '#1a1a2e' : '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    fontWeight: '600'
  };

  const messageStyle = {
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '20px',
    textAlign: 'center'
  };

  const toggleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '15px',
    backgroundColor: theme.backgroundSecondary,
    borderRadius: '4px',
    marginBottom: '20px'
  };

  const toggleButtonStyle = {
    padding: '10px 20px',
    backgroundColor: darkMode ? colors.pink : colors.blue,
    color: darkMode ? '#1a1a2e' : '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600'
  };

  const passwordInputContainerStyle = {
    position: 'relative',
    marginBottom: '20px'
  };

  const passwordToggleStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.1rem',
    padding: '4px'
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={titleStyle}>👤 My Profile</h1>

        {message && (
          <div style={{ ...messageStyle, backgroundColor: colors.green, color: darkMode ? '#1a1a2e' : '#fff', fontWeight: '600' }}>
            {message}
          </div>
        )}
        
        {error && (
          <div style={{ ...messageStyle, backgroundColor: colors.red, color: darkMode ? '#fff' : '#000' }}>
            {error}
          </div>
        )}

        {passwordMessage && (
          <div style={{ ...messageStyle, backgroundColor: colors.green, color: darkMode ? '#1a1a2e' : '#fff', fontWeight: '600' }}>
            {passwordMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
            required
          />

          <label style={labelStyle}>Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={inputStyle}
            required
          />

          {/* Password Change Section */}
          <div style={{ marginBottom: '20px' }}>
            <button
              type="button"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
              style={{
                width: '100%',
                padding: '15px',
                backgroundColor: theme.backgroundSecondary,
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: theme.text,
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              <span>🔒 Change Password</span>
              <span style={{ fontSize: '0.8rem' }}>{showPasswordSection ? '▲' : '▼'}</span>
            </button>

            {showPasswordSection && (
              <div style={{
                padding: '20px',
                backgroundColor: theme.backgroundSecondary,
                borderRadius: '0 0 8px 8px',
                borderTop: 'none',
                marginTop: '-4px'
              }}>
                {passwordError && (
                  <div style={{ ...messageStyle, backgroundColor: colors.red, color: '#fff', marginBottom: '15px' }}>
                    {passwordError}
                  </div>
                )}

                {/* Current Password */}
                <label style={labelStyle}>Current Password</label>
                <div style={passwordInputContainerStyle}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    style={{ ...inputStyle, marginBottom: 0, paddingRight: '45px' }}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    style={passwordToggleStyle}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    title={showCurrentPassword ? 'Hide password' : 'Show password'}
                  >
                    {showCurrentPassword ? '🙈' : '👁️'}
                  </button>
                </div>

                {/* New Password */}
                <label style={labelStyle}>New Password</label>
                <div style={passwordInputContainerStyle}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{ ...inputStyle, marginBottom: 0, paddingRight: '45px' }}
                    placeholder="Minimum 8 characters"
                  />
                  <button
                    type="button"
                    style={passwordToggleStyle}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    title={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? '🙈' : '👁️'}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {newPassword && (
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{
                      display: 'flex',
                      gap: '4px',
                      marginBottom: '5px'
                    }}>
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          style={{
                            flex: 1,
                            height: '4px',
                            borderRadius: '2px',
                            backgroundColor: level <= passwordStrength.level 
                              ? passwordStrength.color 
                              : theme.border
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.8rem', color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}

                {/* Confirm New Password */}
                <label style={labelStyle}>Confirm New Password</label>
                <div style={passwordInputContainerStyle}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ 
                      ...inputStyle, 
                      marginBottom: 0, 
                      paddingRight: '45px',
                      borderColor: confirmPassword && confirmPassword !== newPassword 
                        ? colors.red 
                        : theme.border
                    }}
                    placeholder="Re-enter new password"
                  />
                  <button
                    type="button"
                    style={passwordToggleStyle}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>

                {/* Match indicator */}
                {confirmPassword && (
                  <div style={{ marginBottom: '15px', fontSize: '0.85rem' }}>
                    {confirmPassword === newPassword ? (
                      <span style={{ color: colors.green }}>✓ Passwords match</span>
                    ) : (
                      <span style={{ color: colors.red }}>✗ Passwords do not match</span>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handlePasswordChange}
                  style={{
                    ...buttonStyle,
                    backgroundColor: colors.blue,
                    width: '100%'
                  }}
                >
                  Update Password
                </button>
              </div>
            )}
          </div>

          <label style={labelStyle}>Role</label>
          <input
            type="text"
            value={user?.role === 'SUPER_ADMIN' ? 'Super Administrator' : user?.role === 'ADMIN' ? 'Administrator' : 'Teacher'}
            style={{ ...inputStyle, backgroundColor: theme.backgroundSecondary }}
            disabled
          />

          {/* Availability Settings (Admins only) */}
          {user?.role === 'ADMIN' && (
            <div style={{
              padding: '15px',
              backgroundColor: theme.backgroundSecondary,
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <label style={{ ...labelStyle, marginBottom: '10px' }}>🕐 Default Availability (M-F)</label>
              <p style={{ color: theme.textSecondary, fontSize: '0.85rem', marginTop: 0, marginBottom: '15px' }}>
                Set your typical work hours for scheduling purposes
              </p>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...labelStyle, fontSize: '0.85rem' }}>Start Time</label>
                  <input
                    type="time"
                    value={availabilityStart}
                    onChange={(e) => setAvailabilityStart(e.target.value)}
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...labelStyle, fontSize: '0.85rem' }}>End Time</label>
                  <input
                    type="time"
                    value={availabilityEnd}
                    onChange={(e) => setAvailabilityEnd(e.target.value)}
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Dark Mode Toggle */}
          <div style={toggleContainerStyle}>
            <span style={{ color: theme.text }}>
              🌙 Dark Mode
            </span>
            <button 
              type="button"
              style={toggleButtonStyle} 
              onClick={toggleDarkMode}
            >
              {darkMode ? '☀️ Turn Off' : '🌙 Turn On'}
            </button>
          </div>

          {/* Header Color Picker */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>🎨 Header Theme Color</label>
            <p style={{ color: theme.textSecondary, fontSize: '0.85rem', marginTop: 0, marginBottom: '12px' }}>
              Choose a color for your navigation header
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(5, 1fr)', 
              gap: '10px',
              padding: '15px',
              backgroundColor: theme.backgroundSecondary,
              borderRadius: '8px'
            }}>
              {headerColorOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setHeaderColor(option.value)}
                  title={option.name}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    minHeight: '40px',
                    backgroundColor: option.value,
                    border: customHeaderColor === option.value || (!customHeaderColor && option.value === '#9CD3A5')
                      ? '3px solid #333'
                      : '2px solid transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, border-color 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                />
              ))}
            </div>
            <p style={{ 
              color: theme.textSecondary, 
              fontSize: '0.8rem', 
              marginTop: '8px',
              textAlign: 'center' 
            }}>
              Currently selected: {headerColorOptions.find(o => o.value === (customHeaderColor || '#9CD3A5'))?.name || 'Green (Default)'}
            </p>
          </div>

          <button type="submit" style={buttonStyle}>
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
}

export default Profile;
